/**
 * Environment Badge
 *
 * Displays the current environment (Dev/Staging/Prod) in the header.
 * Uses distinctive colors and a pulsing indicator for non-production environments.
 */
import { cn } from '@respectly/ui';

// Get environment from Vite env or default to development
const ENV = import.meta.env.VITE_ENVIRONMENT || 'development';

interface EnvConfig {
  label: string;
  shortLabel: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  dotColor: string;
  pulse: boolean;
}

const envConfig: Record<string, EnvConfig> = {
  development: {
    label: 'Development',
    shortLabel: 'DEV',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-500/30',
    dotColor: 'bg-amber-500',
    pulse: true,
  },
  staging: {
    label: 'Staging',
    shortLabel: 'STG',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-500/30',
    dotColor: 'bg-purple-500',
    pulse: true,
  },
  production: {
    label: 'Production',
    shortLabel: 'PROD',
    bgColor: 'bg-emerald-500/10',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-emerald-500/30',
    dotColor: 'bg-emerald-500',
    pulse: false,
  },
};

export function EnvironmentBadge() {
  const config = envConfig[ENV] ?? envConfig.development;

  // This should never happen but TypeScript needs the check
  if (!config) {
    return null;
  }

  return (
    <div
      className={cn(
        'hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider border',
        config.bgColor,
        config.textColor,
        config.borderColor
      )}
    >
      {/* Status indicator dot */}
      <span className="relative flex h-2 w-2">
        {config.pulse && (
          <span
            className={cn(
              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
              config.dotColor
            )}
          />
        )}
        <span
          className={cn(
            'relative inline-flex rounded-full h-2 w-2',
            config.dotColor
          )}
        />
      </span>
      {/* Environment label */}
      <span className="hidden lg:inline">{config.label}</span>
      <span className="lg:hidden">{config.shortLabel}</span>
    </div>
  );
}

export default EnvironmentBadge;
